import { useEffect, useMemo, useState } from 'react';
import { X, Home, Calendar, Users, FileText } from 'lucide-react';
import { getErrorMessage, useServices, useToast } from '@/app/providers';
import { ApiError } from '@yawi/infrastructure';
import { useI18n } from '@/shared/i18n';
import { Amenity, Unit } from '@/shared/types';
import { Button } from '../atoms/Button';
import { Select } from '../atoms/Select';
import { SearchableSelect } from '@/core/ui/components/SearchableSelect';
import { AmenityBookingModal } from './AmenityBookingModal';

interface AdminCreateReservationModalProps {
  amenities: Amenity[];
  onClose: () => void;
  onCreated: () => void;
}

const unitLabel = (u: Unit) =>
  u.name || [u.tower, u.number].filter(Boolean).join(' ') || u.id;

export const AdminCreateReservationModal = ({
  amenities,
  onClose,
  onCreated,
}: AdminCreateReservationModalProps) => {
  const { t } = useI18n();
  const toast = useToast();
  const { amenityReservationRepo, unitRepo } = useServices();

  const [units, setUnits] = useState<Unit[]>([]);
  const [unitId, setUnitId] = useState('');
  const [amenityId, setAmenityId] = useState('');
  const [notes, setNotes] = useState('');
  const [generateCharge, setGenerateCharge] = useState(true);
  const [unitUsers, setUnitUsers] = useState<Array<{ isOwner: boolean }>>([]);
  const [proceeded, setProceeded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all: Unit[] = [];
        const limit = 100; // backend MAX_LIMIT
        for (let page = 1; page <= 50; page++) {
          const r = await unitRepo.getUnits({ page, limit, sortBy: 'name', sortOrder: 'asc' });
          all.push(...r.data);
          if (r.data.length < limit) break;
        }
        if (!cancelled) setUnits(all);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unitRepo]);

  useEffect(() => {
    if (!unitId) {
      setUnitUsers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const users = await unitRepo.getUnitUsers(unitId);
        // Active assignments only (no move-out date or future one).
        const active = users.filter(
          (u) => !u.moveOutDate || new Date(u.moveOutDate) > new Date(),
        );
        if (!cancelled) setUnitUsers(active.map((u) => ({ isOwner: u.isOwner })));
      } catch (e) {
        console.error(e);
        if (!cancelled) setUnitUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unitId, unitRepo]);

  const ownerCount = unitUsers.filter((u) => u.isOwner).length;

  const unitOptions = useMemo(
    () => units.map((u) => ({ value: u.id, label: unitLabel(u) })),
    [units],
  );

  const selectedUnit = units.find((u) => u.id === unitId) ?? null;

  // Solo las amenidades que cubren la sección de la unidad (sin secciones = todas, §27).
  const amenityOptions = useMemo(
    () =>
      amenities
        .filter((a) => a.isActive)
        .filter(
          (a) =>
            !selectedUnit ||
            !a.sectionIds?.length ||
            (!!selectedUnit.sectionId && a.sectionIds.includes(selectedUnit.sectionId)),
        )
        .map((a) => ({ value: a.id, label: a.name })),
    [amenities, selectedUnit],
  );

  useEffect(() => {
    if (amenityId && !amenityOptions.some((o) => o.value === amenityId)) setAmenityId('');
  }, [amenityId, amenityOptions]);

  const selectedAmenity = amenities.find((a) => a.id === amenityId) ?? null;

  const handleConfirm = async (data: {
    amenityId: string;
    date: string;
    startTime: string;
    hours: number;
  }) => {
    try {
      await amenityReservationRepo.createReservationForUnit(unitId, {
        ...data,
        notes: notes.trim() || undefined,
        generateCharge,
      });
      toast.success(t('amenities.success'));
      onCreated();
      onClose();
    } catch (e) {
      if (ApiError.isApiError(e) && e.code === 'AMENITY_6006') {
        toast.error(getErrorMessage(e), t('amenities.errors.title'));
      } else if (ApiError.isApiError(e) && e.statusCode === 409) {
        toast.error(t('amenities.errors.slotTaken'), t('amenities.errors.title'));
      } else {
        toast.error(getErrorMessage(e), t('amenities.errors.title'));
      }
    }
  };

  if (proceeded && selectedAmenity && selectedUnit) {
    return (
      <AmenityBookingModal
        amenity={selectedAmenity}
        memberId={selectedUnit.id}
        memberName={unitLabel(selectedUnit)}
        sectionId={selectedUnit.sectionId}
        adminMode
        onClose={() => setProceeded(false)}
        onConfirm={handleConfirm}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
        <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h3 className="font-bold text-slate-900 text-lg leading-tight">Nueva reservación</h3>
            <p className="text-xs text-slate-400">Selecciona la unidad y la amenidad</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-300 hover:text-slate-600 transition-colors rounded-lg hover:bg-slate-100 p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          <div className="flex items-start gap-2">
            <Home size={16} className="text-slate-400 mt-7 shrink-0" />
            <div className="flex-1">
              <SearchableSelect
                label="Unidad"
                value={unitId || null}
                options={unitOptions}
                onChange={(v) => setUnitId(v ?? '')}
                placeholder="Busca una unidad..."
                isClearable
              />
              <p className="mt-1 text-xs text-slate-400">
                El cargo se generará al titular de la unidad.
              </p>
              {unitId && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                  <Users size={13} className="text-slate-400 shrink-0" />
                  <span>
                    {unitUsers.length} residente{unitUsers.length === 1 ? '' : 's'} activo
                    {unitUsers.length === 1 ? '' : 's'}
                    {ownerCount > 0 && ` · ${ownerCount} titular${ownerCount === 1 ? '' : 'es'}`}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Calendar size={16} className="text-slate-400 mt-7 shrink-0" />
            <div className="flex-1">
              <Select
                label="Amenidad"
                value={amenityId}
                options={amenityOptions}
                onChange={setAmenityId}
                placeholder="Selecciona una amenidad"
                required
              />
            </div>
          </div>

          <div className="flex items-start gap-2">
            <FileText size={16} className="text-slate-400 mt-7 shrink-0" />
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Notas (opcional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder="Visible para los residentes de la unidad"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 pl-6 text-sm text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={generateCharge}
              onChange={(e) => setGenerateCharge(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
            />
            Generar cargo si la amenidad tiene costo
          </label>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 bg-white">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => setProceeded(true)}
            className="flex-1"
            disabled={!unitId || !amenityId}
          >
            Continuar
          </Button>
        </div>
      </div>
    </div>
  );
};
